import { FeedHeader } from '~publish/components/Composer/InstagramPreview/FeedHeader'
import { useState, useCallback } from 'react'
import type { JSX } from 'react'

import { UnstyledButton } from '@buffer-mono/popcorn'
import { PostFields } from '@buffer-mono/reminders-config'

import type {
  Gif,
  Image,
} from '~publish/legacy/composer/composer/entities/factories'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseInstagramLinks } from '~publish/legacy/utils/channels/instagram/helpers'

import {
  BookmarkIcon,
  CommentIcon,
  LikeIcon,
  RepostIcon,
  ShareIcon,
} from '../../../../assets/instagram'
import { useExpandablePreviewText } from '../../../Common/hooks/useExpandablePreviewText'
import { EmptyMediaPlaceholder, PostWrapper } from '../../../Common/styles'
import type { InstagramContent, ProfilePreview } from '../../../types'
import { ImageCarousel } from '../ImageCarousel'
import type { ImageCarouselSlide } from '../ImageCarousel'
import { ImagePreview } from '../ImagePreview'
import { VideoPlayer } from '../VideoPlayer'
import { getDotSize, getVisibleDotIndices } from './utils'

import styles from './Post.module.css'

// A gif has no dedicated carousel slide of its own — it renders as an image,
// same as everywhere else a gif reaches the wire (see mapAssets.ts).
const gifAsImage = (gif: Gif): Image => ({
  url: gif.url,
  width: gif.width,
  height: gif.height,
  mediaType: gif.mediaType,
})

const Ellipse = ({
  selected = false,
  size = 'medium',
}: {
  selected?: boolean
  size?: string
}): JSX.Element => (
  <div
    data-testid="ellipse"
    data-size={size}
    className={selected ? styles.ellipseSelected : styles.ellipse}
  />
)

const Ellipses = ({
  visible,
  totalImages,
  currentIndex = 0,
}: {
  visible: boolean
  totalImages: number
  currentIndex?: number
}): JSX.Element => {
  // For 4 or fewer images, show all dots
  if (totalImages <= 4) {
    const imagePositions = Array.from(
      { length: totalImages },
      (_, dotIndex) => dotIndex + 1,
    )
    return (
      <div
        className={styles.ellipsesWrapper}
        data-testid="ellipses"
        data-hidden={!visible}
      >
        {imagePositions.map((imagePosition) => (
          <Ellipse
            key={`image-${imagePosition}-indicator`}
            selected={imagePosition - 1 === currentIndex}
          />
        ))}
      </div>
    )
  }

  // For more than 4 images, show a sliding window of max 5 dots
  const visibleIndices = getVisibleDotIndices(currentIndex, totalImages)

  return (
    <div
      className={styles.ellipsesWrapper}
      data-testid="ellipses"
      data-hidden={!visible}
    >
      {visibleIndices.map((imageIndex) => (
        <Ellipse
          key={imageIndex}
          selected={imageIndex === currentIndex}
          size={getDotSize(imageIndex, currentIndex)}
        />
      ))}
    </div>
  )
}

const FirstComment = (): JSX.Element => (
  <div className={styles.firstComment}>View 1 comment</div>
)

const InstagramPost = ({
  content,
  profile,
}: {
  content: InstagramContent
  profile: ProfilePreview
}): JSX.Element => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const { username } = profile
  const { text, images, gif, video, channelData } = content
  const comment = channelData?.instagram?.comment_text

  const { textToDisplay, isTruncated, isExpanded, expand } =
    useExpandablePreviewText({
      channel: 'instagram',
      text,
    })

  // `content.assets` is the ordered image/video/gif/document source of
  // truth when the caller provides it. Callers that haven't adopted it yet
  // (older fixtures, network content types without an assets field) fall
  // back to the legacy images-array-or-gif-fallback plus a separate video
  // field, reproducing the pre-mixed-media behaviour exactly. Either way,
  // `slides` is the single source every render branch below derives from.
  const slides: ImageCarouselSlide[] = content.assets?.length
    ? content.assets.reduce<ImageCarouselSlide[]>((acc, asset) => {
        if (asset.kind === 'image') {
          acc.push({ kind: 'image', image: asset.image })
        } else if (asset.kind === 'gif') {
          acc.push({ kind: 'image', image: gifAsImage(asset.gif) })
        } else if (asset.kind === 'video') {
          acc.push({ kind: 'video', video: asset.video })
        }
        return acc
      }, [])
    : [
        ...(images?.length
          ? images.map((image) => ({ kind: 'image' as const, image }))
          : gif
            ? [{ kind: 'image' as const, image: gifAsImage(gif) }]
            : []),
        ...(video ? [{ kind: 'video' as const, video }] : []),
      ]

  const totalSlides = slides.length
  const hasCarousel = totalSlides > 1
  const singleSlide = totalSlides === 1 ? slides[0] : null

  const handleImageIndexChange = useCallback((index: number) => {
    setCurrentImageIndex(index)
  }, [])

  const stickerProducts =
    content.isReminder &&
    content.selectedStickers?.includes(PostFields.PRODUCTS)
      ? content?.channelData?.instagram?.products
      : ''

  return (
    <PostWrapper>
      <FeedHeader content={content} profile={profile} />
      <div className={styles.body}>
        {singleSlide?.kind === 'video' && (
          <VideoPlayer video={singleSlide.video} />
        )}
        {hasCarousel && (
          <ImageCarousel
            slides={slides}
            stickerProducts={stickerProducts}
            onIndexChange={handleImageIndexChange}
          />
        )}
        {singleSlide?.kind === 'image' && (
          <ImagePreview
            key={singleSlide.image.url}
            image={singleSlide.image}
            stickerProducts={stickerProducts}
          />
        )}
        {totalSlides === 0 && (
          <EmptyMediaPlaceholder service="instagram" className={styles.empty} />
        )}
        <div className={styles.actionsWrapper}>
          <LikeIcon size={20} />
          <CommentIcon size={20} />
          <RepostIcon size={20} />
          <ShareIcon size={20} />
          <Ellipses
            totalImages={totalSlides}
            visible={hasCarousel}
            currentIndex={currentImageIndex}
          />
          <BookmarkIcon size={20} />
        </div>
        {(text || comment) && (
          <div className={styles.caption}>
            {text && (
              <>
                <span className={styles.captionUsername} translate="no">
                  {username}
                </span>
                <LinkifiedText
                  className={styles.linkifiedText}
                  links={parseInstagramLinks('post', textToDisplay)}
                  size="mini"
                  whitespace="pre-wrap"
                  newTab
                >
                  {textToDisplay}
                </LinkifiedText>
                {isTruncated && !isExpanded && (
                  <UnstyledButton
                    onClick={expand}
                    className={styles.seeMoreButton}
                  >
                    ... more
                  </UnstyledButton>
                )}
              </>
            )}
            {comment && <FirstComment />}
          </div>
        )}
      </div>
    </PostWrapper>
  )
}

export default InstagramPost
